import React from "react";
import {
    Accordion, AccordionButton, AccordionIcon, AccordionItem, AccordionPanel,
    Alert, AlertIcon, Badge, Box, Flex, Text, useColorModeValue,
} from "@chakra-ui/react";

const ALERT_STATUS = { success: "success", warning: "warning", error: "error" };
const SCHEME = { success: "green", warning: "orange", error: "red" };

/**
 * Renders the summary badge row showing migration totals.
 * @param {object} props
 * @param {object} props.data - Migration data with counts
 * @returns {React.ReactElement}
 */
function MigrationSummaryChips({ data }) {
    return (
        <Flex mb="20px" gap="10px" flexWrap="wrap">
            <Badge colorScheme="blue" variant="outline" borderRadius="6px" px="8px" py="2px">Total found: {data.total_declined_found}</Badge>
            <Badge colorScheme="green" variant="outline" borderRadius="6px" px="8px" py="2px">Migrated: {data.migrated_count}</Badge>
            <Badge colorScheme="orange" variant="outline" borderRadius="6px" px="8px" py="2px">Skipped: {data.skipped_count}</Badge>
            <Badge colorScheme="red" variant="outline" borderRadius="6px" px="8px" py="2px">Errors: {data.errors_count}</Badge>
        </Flex>
    );
}

/**
 * Renders a reusable migration accordion section with scrollable alert items.
 * @param {object} props
 * @param {Array} props.items - Array of migration items to display.
 * @param {string} props.severity - Severity key (success, warning, error).
 * @param {string} props.title - Accordion header title text.
 * @param {Function} props.renderAlert - Render function for each item's alert content.
 * @param {boolean} [props.defaultOpen] - Whether the accordion starts expanded.
 * @returns {React.ReactElement|null} The accordion or null if no items.
 */
function MigrationAccordionSection({ items, severity, title, renderAlert, defaultOpen }) {
    const borderColor = useColorModeValue("gray.200", "whiteAlpha.300");
    if (!items || items.length === 0) return null;
    return (
        <Accordion allowToggle defaultIndex={defaultOpen ? 0 : undefined} mb="14px">
            <AccordionItem border="1px solid" borderColor={borderColor} borderRadius="12px">
                <AccordionButton borderRadius="12px">
                    <Box flex="1" textAlign="left">
                        <Text fontWeight="600" color={`${SCHEME[severity]}.500`}>{title} ({items.length})</Text>
                    </Box>
                    <AccordionIcon />
                </AccordionButton>
                <AccordionPanel>
                    <Box maxH="300px" overflow="auto">
                        {items.map((item, index) => (
                            <Alert key={index} status={ALERT_STATUS[severity]} borderRadius="10px" mb="8px" alignItems="flex-start"><AlertIcon />{renderAlert(item)}</Alert>
                        ))}
                    </Box>
                </AccordionPanel>
            </AccordionItem>
        </Accordion>
    );
}

/**
 * Renders the alert content for a migrated contact card item.
 * @param {object} item - Migrated item with contact_card_id, reservation_id, event_date, old_status, new_status.
 * @returns {React.ReactElement} Alert body with card and reservation details.
 */
function renderMigratedAlert(item) {
    return (
        <Box>
            <Text fontSize="sm"><strong>Card #{item.contact_card_id}</strong> - Reservation #{item.reservation_id}</Text>
            <Text fontSize="xs" opacity={0.8}>Event date: {item.event_date} | {item.old_status} &rarr; {item.new_status}</Text>
        </Box>
    );
}

/**
 * Renders the alert content for a skipped contact card item.
 * @param {object} item - Skipped item with contact_card_id, reason, and optional event_date.
 * @returns {React.ReactElement} Alert body with card ID and skip reason.
 */
function renderSkippedAlert(item) {
    return (
        <Box>
            <Text fontSize="sm"><strong>Card #{item.contact_card_id}</strong></Text>
            <Text fontSize="xs" opacity={0.8}>Reason: {item.reason}{item.event_date && ` | Event date: ${item.event_date}`}</Text>
        </Box>
    );
}

/**
 * Renders the alert content for an error item from migration.
 * @param {object} item - Error item with contact_card_id and error message.
 * @returns {React.ReactElement} Alert body with card ID and error text.
 */
function renderErrorAlert(item) {
    return (
        <Box>
            <Text fontSize="sm"><strong>Card #{item.contact_card_id}</strong></Text>
            <Text fontSize="xs" opacity={0.8}>{item.error}</Text>
        </Box>
    );
}

/**
 * Displays the full migration results including summary badges and
 * accordion sections for migrated, skipped, and errored items.
 * @param {object} props
 * @param {object} props.data - The contact_cards_migration response data
 * @returns {React.ReactElement|null}
 */
function MigrationResultsContent({ data }) {
    const titleColor = useColorModeValue("secondaryGray.900", "whiteAlpha.900");
    if (!data) return null;
    return (
        <Box>
            <Text fontSize="md" fontWeight="700" color={titleColor} mb="14px">
                Contact cards migration (DECLINED &rarr; EXPIRED)
            </Text>
            <MigrationSummaryChips data={data} />
            <MigrationAccordionSection items={data.migrated} severity="success"
                title="Migrated contact cards" renderAlert={renderMigratedAlert} defaultOpen />
            <MigrationAccordionSection items={data.skipped} severity="warning"
                title="Skipped contacts" renderAlert={renderSkippedAlert} />
            <MigrationAccordionSection items={data.errors} severity="error"
                title="Errors" renderAlert={renderErrorAlert} />
        </Box>
    );
}

export default MigrationResultsContent;
